/** Known LaunchDarkly flag keys, centralised so the string literal lives in
 *  one place across the codebase. Keys are kebab-case to match the LD project
 *  convention (cf. `custom-system-roles`). */

/** Gates the intake submission-logic runtime (requester page navigator + the
 *  submit rework it builds toward). Default **off** in all environments and
 *  treated as off when absent, so the incremental epic can land slice by slice
 *  without changing the live single-shot requester flow until it is
 *  feature-complete. Epic #2698, slice S1 #2705. */
export const INTAKE_SUBMISSION_LOGIC_FLAG = "intake-submission-logic";

/** Project Leads must be connected to the project (NG-2967, epic NG-2959, User
 *  Access Model spec 5.1 and 5.2). Same key as the backend's `CONNECTED_LEADS`
 *  (`cognota/core/launchdarkly/flags.py`), which refuses a new Lead who is not
 *  connected and reports Leads who lost their connection, who stay Lead. The
 *  frontend half narrows the Lead picker to connected people and shows the
 *  warning. Off: any eligible member, no warning. Defaults **off** and is
 *  treated as off when absent. */
export const TARGET_MODEL_CONNECTED_LEADS_FLAG = "target-model-connected-leads";

/** The Target Model's team picks for projects and programs (NG-2969, epic
 *  NG-2959). Same key as the backend's `CONNECTED_TEAM_PICKS`
 *  (`cognota/core/launchdarkly/flags.py`), which enforces it per workspace: a
 *  non-Admin's Home Team must be a team they are on, and a Delivery Team or
 *  Team Access team they add must be one they are connected to (their teams
 *  and the teams below them). The frontend half filters the pickers to match.
 *  Off: any team in the workspace, as before. Defaults **off** and is treated
 *  as off when absent. */
export const TARGET_MODEL_CONNECTED_TEAM_PICKS_FLAG = "target-model-connected-team-picks";

/** Roles and Permissions Target Model (epic NG-2959, NG-2978, spec 13.2): the Share
 *  modal's "Add a team" picker and its "Teams with access" list. A team share lets
 *  the team's members and the members of every team above it view the dashboard.
 *  Same key as the backend's `TARGET_MODEL_DASHBOARD_TEAM_SHARES`, whose
 *  `team-shares` routes 404 while it is off. ON in dev and staging, OFF in
 *  production; treated as off when absent. */
export const TARGET_MODEL_DASHBOARD_TEAM_SHARES_FLAG = "target-model-dashboard-team-shares";

/** Gates the experimental team permission graph: the admin-only "Team access"
 *  entry on the team settings Members tab and its modal. Created manually in the
 *  LD UI; defaults **off** and is treated as off when the flag is absent, so it
 *  can merge dark and be turned on per-environment for a product review. */
export const EXPERIMENTAL_TEAMS_GRAPH_FLAG = "experimental-teams-graph";

/** Gates the dialog that offers to move a task's linked tasks after its date is
 *  saved outside the Gantt (NG-1283): the table's Start and Due cells, the side
 *  panel, the task page, the subtasks table and a calendar drag. Created manually
 *  in the LD UI; defaults **off** and is treated as off when absent. Frontend only:
 *  the preview endpoint it calls is additive and stays live, and off, a date edit
 *  saves exactly as before. */
export const DEPENDENT_DATE_SHIFT_FLAG = "work-item-dependent-date-shift";

/** Gates every goals surface: the sidebar Strategy section and its Goals item,
 *  the `/:slug/goals` management page, and goals as a project field (the picker
 *  in project settings → General, the Goals row in the team project side panel,
 *  and the reference prefetch the project overview loader makes for it).
 *
 *  Goals reached every environment as soon as NG-60/NG-67 landed, but edit and
 *  delete are still open, so the flag is what decides where a half-built surface
 *  is visible: **on** in local/development/staging, **off** in production, and
 *  treated as off when absent. NG-153.
 *
 *  Reports read this flag too (NG-622): the report builder's column picker is
 *  populated from the BACKEND field catalog, which knows nothing about
 *  LaunchDarkly, so the Goals columns are filtered client-side here rather than
 *  being absent from the response. Who may *see* goal data is a separate,
 *  server-side question answered by `goal.read`. */
export const GOALS_FLAG = "goals";

/** Gates the program metrics surfaces: the Progress / Health / Effort columns on
 *  the workspace Programs list, and the KPI band (Projects Overdue, Program
 *  Progress, Hours Logged + the status/health/effort donuts) on the program
 *  overview page. NG-2229 rides it too: sortable program tables, the alignment
 *  strip and the Details sheet.
 *
 *  Only the *read* surfaces are gated. The rollup table, its staleness marking
 *  and the 5-minute drain keep running with the flag off, so the numbers are
 *  already correct the moment it is switched on rather than starting at an
 *  em-dash for a drain cycle; the `metrics` block on the program serializer is
 *  additive and inert when nothing renders it.
 *
 *  Created manually in the LD UI; defaults **off** and is treated as off when
 *  the flag is absent. NG-285 (epic), NG-289 / NG-290 (the gated surfaces). */
export const PROGRAM_METRICS_FLAG = "program-metrics";

/** Gates the Cornerstone (CSOD) LMS integration, and only that.
 *
 *  NG-942 S1.5 said Cornerstone is never visible while 360Learning is off,
 *  because 360Learning built the connection, catalog, link / unlink and results
 *  surfaces Cornerstone reuses. That rule is now satisfied for good: 360Learning
 *  shipped to everyone and its flag was retired (NG-2072), so there is nothing
 *  left to order this key against. Cornerstone is always named in the catalog
 *  and connects only while this key is on.
 *
 *  Read it through `connector-catalog.ts`, where it is Cornerstone's
 *  `connectFlag`, rather than at a call site: `connectorsFor` and
 *  `availabilityOf` are what turn it into Available or Planned.
 *
 *  Created in LaunchDarkly on 2026-09-14 and treated as off when absent. NG-941
 *  (epic), NG-942 (this flag).
 *
 *  What it gates, since NG-2242: whether Cornerstone is Available in the Integration
 *  Library, which is what opens its connector page (the route refuses it while
 *  Planned), its Connect form, and its row on Installed. With it on, the LMS sentences
 *  with no connection in hand (the Delivery and picker empty states, the
 *  recommended-content copy) stop naming 360Learning, because they cannot tell which
 *  vendor they are about (`workspaceLmsVendor` in `lib/integrations/lms-vendors.ts`).
 *  The backend connects Cornerstone only where `CORNERSTONE_API_SCOPES` is set, and
 *  refuses the form otherwise, so an environment needs both. */
export const LMS_INTEGRATION_CORNERSTONE_FLAG = "lms-integration-cornerstone";

/** Gates the private draft-requests surface in the requester fill flow
 *  (NG-702, epic NG-657): the "Save draft" action while filling a form, the
 *  resume-from-draft path (`?draft=` on the fill route), routing submit of a
 *  resumed draft through the draft-submit endpoint, and the "Your drafts"
 *  section on the request-type picker. The backend draft endpoints are ungated
 *  and RBAC-guarded — the flag only decides where the UI surfaces. Defaults
 *  **off** and is treated as off when absent, so the lane lands dark and turns
 *  on per environment. */
export const PRIVATE_DRAFT_REQUESTS_FLAG = "private-draft-requests";
/** Gates the "Create project" button on a request's Linked Projects tab
 *  (NG-701): the Request Owner's entry into the standard project-creation
 *  dialog, pre-linked to the request. Only the button is gated — the tab, the
 *  table and the backend convert endpoints are ungated and RBAC-guarded, and
 *  the `can_create_project` server verdict (owner-only) still decides who could
 *  ever see it. Defaults **off** and is treated as off when absent, so the
 *  slice lands dark and turns on per environment once the epic's lanes are
 *  reviewed together. NG-701 (epic NG-657). */
export const REQUEST_LINKED_PROJECTS_CREATE_FLAG = "request-linked-projects-create";
/** Gates the persisted table sort and column filters (NG-517): the `sorting` and
 *  `columnFilters` slices of the per-browser table preferences, on both the
 *  hook path (`useTableSorting` / `useTableColumnFilters`, which every
 *  client-sorted list surface takes) and the store path (the ProjectStore,
 *  IntakeStore and ReportStore reactions that persist their server-sorted
 *  lists).
 *
 *  Only those two slices are gated. `columnOrder`, `columnVisibility` and
 *  `columnSizing` (NG-451) keep persisting for everyone — they predate this work
 *  and are not part of it.
 *
 *  With the flag off a table sorts and filters exactly as it did before NG-517:
 *  the controls still work, the choice just does not survive a reload, and
 *  nothing is read from or written to localStorage for those two slices. So the
 *  work lands dark and turns on per environment. Created in the LD project
 *  `next-gen` on 2026-09-02, **off** in all four environments (local,
 *  development, staging, production), and treated as off when absent. NG-517. */
export const PERSISTED_TABLE_PREFS_FLAG = "persisted-table-prefs";

/** Gates the whole Microsoft Teams notification-channel surface (epic NG-886): the
 *  workspace settings → Integrations entry that consents the tenant, the
 *  per-person "connect my Teams" control, and the category picker that narrows
 *  which notifications go out.
 *
 *  Off is the only safe default here, because the thing on the other side of the
 *  flag is a message in somebody's Teams chat: a half-wired channel does not
 *  degrade quietly the way a hidden page does, it pushes cards. So the flag is
 *  **off** everywhere and treated as off when absent, and the backend substrate
 *  (NG-952) lands first, dark and unreachable — no connection row exists, so the
 *  emit path finds nothing to deliver to whatever the flag says. NG-952 (this
 *  slice), epic NG-886.
 *
 *  Read by the Integration Library route and its Microsoft Teams card (NG-1096) —
 *  the first call sites. The knip dark-flag exemption this export carried while the
 *  key was registered a PR ahead of its UI is therefore deleted: left behind, it
 *  would let a genuinely dead flag key sit in this file unnoticed. */
export const MS_TEAMS_INTEGRATION_FLAG = "ms-teams-integration";

/** Gates every customer-facing surface of the Custom System Roles epic
 *  (NG-1233) as its slices land: the System Role vocabulary (NG-1246), the
 *  permission-gated Roles surface (NG-1254), the System Roles table, create
 *  modal and edit page (NG-1255/1256/1259/1257/1271), enforcement following the
 *  toggles (NG-1258), the Members/Access/export changes (NG-1247/1249/1250),
 *  and the removal of the user menu's Workspace Invitations item (NG-1248).
 *  NG-1248 also hid the profile Danger Zone behind it; NG-935 removed that
 *  for everyone, so the profile no longer reads this flag.
 *
 *  One key for the whole epic rather than one per story, because the slices are
 *  only coherent together: the rename alone renames a page whose Create button
 *  is still inert, and the create modal alone would ship under the old
 *  vocabulary. Off is the right default because the epic changes what a role
 *  *means* — the backend now derives a member's License Tier from their System
 *  Role — so the surface stays dark until the whole of NG-1233 passes QA and
 *  product picks the moment.
 *
 *  Its first consumer is the vocabulary overlay, so this key needs no
 *  ahead-of-its-call-site exemption tag: `SystemRoleVocabularySync` imports
 *  this key today.
 *
 *  Created manually in the LD UI; defaults **off** in all environments and is
 *  treated as off when absent. NG-1233 (epic), NG-1246 (first slice). */
export const CUSTOM_SYSTEM_ROLES_FLAG = "custom-system-roles";

/** Projects have Members only (NG-3023, epic NG-2959, spec 5.9). Same key as the
 *  backend's `MEMBERS_ONLY` (`cognota/core/launchdarkly/flags.py`), which refuses a new
 *  project Viewer. The frontend half drops the overview's Viewers row and calls a
 *  project's Admin its "Project Lead" on the Members page, and drops the Members page's
 *  email-invite tab/toggle and its Pending invitations panel (NG-3223), leaving only the
 *  existing-members picker. Defaults **off**; ON in dev and staging, OFF in production
 *  until product turns it on. */
export const TARGET_MODEL_MEMBERS_ONLY_FLAG = "target-model-members-only";

/** The Target Model's request Home Team (NG-2971 and NG-3040, epic NG-2959, spec 9.1).
 *  Same key as the backend's `TARGET_MODEL_REQUEST_HOME_TEAM`
 *  (`cognota/core/launchdarkly/flags.py`), which lets a triager move a request between
 *  teams. The frontend half calls an intake workflow's owning team "Home Team" instead of
 *  "Managing Team" (builder field, pending-changes list, forms list column), so the
 *  wording changes with the behaviour. Defaults **off**; ON in dev and staging, OFF in
 *  production until product turns it on. */
export const TARGET_MODEL_REQUEST_HOME_TEAM_FLAG = "target-model-request-home-team";

/** The Target Model's request visibility (NG-2972 and NG-3045, epic NG-2959, spec 9.5).
 *  Same key as the backend's `TARGET_MODEL_REQUEST_VISIBILITY`
 *  (`cognota/core/launchdarkly/flags.py`), which lets a request's Home Team and the teams
 *  above it see it, instead of its form's managing team and the teams below. The frontend
 *  half rewords the builder's owning-team hint to match, so the copy changes with the
 *  behaviour. Defaults **off**; ON in dev and staging, OFF in production until product
 *  turns it on. */
export const TARGET_MODEL_REQUEST_VISIBILITY_FLAG = "target-model-request-visibility";

/** Roles and Permissions Target Model (epic NG-2959, NG-2979, spec 14): saved views.
 *  The Views page splits its saved views into My views and Entire Workspace (14.4).
 *  Same key as the backend's `TARGET_MODEL_SAVED_VIEW_ACCESS`, which gates who may
 *  open, publish, edit and delete a view. OFF in every environment for now (the dev
 *  nightly's `shared-view-link.spec.ts` asserts the open-by-link behaviour the
 *  backend half reverses); treated as off when absent. */
export const TARGET_MODEL_SAVED_VIEW_ACCESS_FLAG = "target-model-saved-view-access";

/** Roles and Permissions Target Model (epic NG-2959, NG-2980, spec 14.7): share a saved
 *  view with specific people or teams from its card's Share action, and a Shared with
 *  me section on the Views page (14.4). A team share lets the team's members and the
 *  members of every team above it open the view. Same key as the backend's
 *  `TARGET_MODEL_SAVED_VIEW_SHARING`, whose `shares` route 404s while it is off. ON in
 *  dev and staging, OFF in production; treated as off when absent. The section shows
 *  only where the Views page is split, behind `TARGET_MODEL_SAVED_VIEW_ACCESS_FLAG`. */
export const TARGET_MODEL_SAVED_VIEW_SHARING_FLAG = "target-model-saved-view-sharing";

/** Roles and Permissions Target Model (epic NG-2959, NG-3024, spec 6.2): the Team Lead
 *  permissions section of Workspace Settings > General, where an Administrator chooses
 *  which team-owned setup areas Team Leads manage for their teams. Same key as the
 *  backend's `TARGET_MODEL_TEAM_SETUP_AREAS`, which makes the choice take effect.
 *  Defaults **off**; ON in dev and staging, OFF in production until product turns it on. */
export const TARGET_MODEL_TEAM_SETUP_AREAS_FLAG = "target-model-team-setup-areas";

/** Gates the Rebalance entry points on the Capacity People view: the action on
 *  an over-allocated person's row and the matching action on an over-allocation
 *  risk chip. The proposal dialog and its backend endpoint remain in place, so
 *  turning the flag back on restores the existing workflow without a deploy.
 *  Defaults **off** and is treated as off when absent, so Rebalance stays hidden
 *  while product decides when to make it available again. */
export const CAPACITY_REBALANCE_FLAG = "capacity-rebalance";

/** Gates the deferral of the Projects page's reference banks (NG-1566): with
 *  the flag on, the six banks that feed only the table's cells and the filter
 *  popover's facets — members, teams, programs, goals, custom fields and
 *  category fields — are fetched when the table is the active layout or the
 *  filter panel is first opened, instead of on mount whatever the layout. Off,
 *  all six go out on mount exactly as they did before, so a board open still
 *  pays for six responses it never reads.
 *
 *  It gates the filter popover's loading state with them, and has to: deferral
 *  is what opens the gap the placeholders fill. With the flag off the banks are
 *  in flight from mount, so the panel shows **no placeholder at all** — which is
 *  what it does on `main` today, and what makes this dark rather than merely
 *  quieter.
 *
 *  Deliberately NOT gated: the category-values cache added alongside it. That
 *  one stops a refetch of data the page already holds and changes nothing a user
 *  can see, so it is the "internals / perf" case that ships unflagged.
 *
 *  A flag for a perf change because the deferral has one visible consequence —
 *  the filter panel fills in rather than arriving full — and product decides
 *  when that is on. Created in the LD project `next-gen` on 2026-09-22, **off**
 *  in all four environments (local, development, staging, production), and
 *  treated as off when absent. NG-1566. */
export const PROJECTS_DEFERRED_REFERENCE_BANKS_FLAG = "projects-deferred-reference-banks";

/** Lead-only actions (NG-2968, epic NG-2959, User Access Model spec 5.4 to 5.6).
 *  Same key as the backend's `LEAD_ONLY_ACTIONS` (`cognota/core/launchdarkly/flags.py`),
 *  which refuses a project's Leads, teams, visibility or deletion to anyone but
 *  its Leads and Admins. The frontend half (NG-3089) hides those controls from
 *  everyone else, through `lib/permissions/project-lead-actions.ts`. Off: the role
 *  permission alone, as before. Defaults **off** and is treated as off when absent. */
export const TARGET_MODEL_LEAD_ONLY_ACTIONS_FLAG = "target-model-lead-only-actions";

/** Gates the login screen's new "SSO unavailable" state (NG-1370, FR26/FR28,
 *  epic NG-1367). Today, an SSO-enabled domain whose identity provider is
 *  broken or unconfigured silently bounces the user back to the email-entry
 *  screen with no visible explanation (the SSO-initiate failure redirects
 *  with `?error=`, a query param the login screen's error banner has never
 *  read — it only reads `?error_code=`). This flag decides which of two
 *  read-only backend facts to render for that case: off, "SSO" (today's
 *  auto-redirect into the same silent failure, pixel-identical to current
 *  behavior); on, "SSO_UNAVAILABLE" (a support-contact banner instead).
 *
 *  No backend behavior is gated — `EmailCheckEndpoint` always reports the
 *  factual live/unavailable state; this flag only decides which one the
 *  frontend acts on. Pre-auth, so it evaluates against an anonymous LD
 *  context rather than a signed-in user's. Created manually in the LD UI;
 *  defaults **off** and is treated as off when the flag is absent. */
export const SSO_LOGIN_ROUTING_FLAG = "sso-login-routing";

/** Gates reading the `?error=` query param the SSO redirect flow
 *  (`error_redirect` in `apps/backend/cognota/sso/views_auth.py`) sends on a
 *  refused login — `sso_account_not_linked` (the NG-1686 cross-tenant guard),
 *  `oidc_provider_error`, `saml_provider_error`, `sso_error`,
 *  `sso_not_configured`, `domain_not_configured`, `email_required`,
 *  `invalid_email`. That param is a different scheme from `?error_code=`
 *  (`authErrorHandler`'s numeric codes) and `auth-root.tsx` has never read it,
 *  so today every one of these bounces the user back to a blank, unexplained
 *  email form. Off reproduces that silent failure byte-for-byte; on renders
 *  the matching `auth.error.<slug>` copy through the existing error banner,
 *  with the email form still visible underneath as the way to sign in another
 *  way. No backend behavior is gated — the backend has sent this param
 *  unconditionally since the NG-1686 hotfix regardless of this flag.
 *
 *  Created manually in the LD UI; defaults **off** and is treated as off when
 *  the flag is absent. NG-1710. */
export const SSO_ERROR_MESSAGING_FLAG = "sso-error-messaging";

/** Gates surfacing the backend's specific rejection message on the SAML/OIDC
 *  provider forms (NG-1927) when NG-1702's single-active-provider rule (FR21)
 *  rejects activating a second identity provider. Off: the existing generic
 *  "Failed to create/update provider" toast for every error, unchanged. On:
 *  that one documented rejection shows its own message ("Another identity
 *  provider is already active for this workspace. Disable it before
 *  activating another."), telling the admin how to remediate instead of
 *  leaving them to guess; every other error still falls back to the generic
 *  toast. Created manually in the LD UI; defaults **off** and is treated as
 *  off when the flag is absent. Do NOT restore the pre-NG-1702 silent
 *  auto-disable behavior regardless of this flag's state — that reject-vs-
 *  auto-switch call was made deliberately on NG-1702. */
export const SSO_PROVIDER_ERROR_DETAIL_FLAG = "sso-provider-error-detail";

/** Gates the read-only state on a record the viewer can open but not edit
 *  (NG-1872): a collapsed "You can read this … but not change it." banner with a
 *  "Why?" disclosure naming the viewer's System Role, over fields that render as
 *  disabled controls rather than as plain text or live inputs. One key for the
 *  epic's four surfaces (Requests NG-1875, Projects NG-1876, Programs NG-1877,
 *  Work items NG-1878), because the PRD's point is one consistent pattern: half
 *  the surfaces explaining themselves and half not is the confusion it removes.
 *
 *  It ships alongside Custom System Roles, which is what makes "can view but not
 *  edit" common, so product turns it on with that; merged is not released.
 *  Created manually in the LD UI; defaults **off** in all four environments
 *  (local, development, staging, production) and is treated as off when absent. */
export const READ_ONLY_INDICATORS_FLAG = "read-only-indicators";

/** Gates the autosave status pill under the event description (NG-2833). The
 *  rich-HTML description round trip itself is the BUG FIX and ships unflagged —
 *  editing an event description no longer escapes its formatting into literal
 *  tags — so only the NEW visible indicator (the Saving/Saved/Save failed pill
 *  under the editor) rides this flag and merges dark, per the rule that any
 *  visible change ships behind a LaunchDarkly flag.
 *
 *  Created manually in the LD UI; defaults **off** in all environments and is
 *  treated as off when absent. */
export const EVENT_DESCRIPTION_SAVE_STATUS_FLAG = "event-description-save-status";

/** Gates the workspace-template SHARING vocabulary (NG-1355): the save dialog's
 *  "Shared with GTM" / "Private" choice, and the same two values on the templates
 *  table's tag and its Scope filter. A private template also loses its "Mark as
 *  default for new workspaces" action, which the backend refuses for one.
 *
 *  Off: exactly what shipped before it, "Global" / "Restricted", with
 *  "Restricted" posted as the deprecated alias the backend stores as `global`.
 *
 *  The dialog posts `private` with the flag on, which a backend older than
 *  NG-1355's sharing change refuses, so the flag must not go on in an
 *  environment until that backend is deployed there. Created manually in the
 *  LD UI on 2026-09-29, once that backend was on dev: **on** in development and
 *  staging, **off** in production and local, and treated as off when absent. */
export const WORKSPACE_TEMPLATE_SHARING_FLAG = "workspace-template-sharing";

/** Gates the Team Lead create affordances (NG-2176, under NG-1992): the create
 *  controls #5842's backend enabled but never surfaced — "New Project" on the
 *  Projects list and on a team's Projects tab, and "Create team" on My Teams —
 *  for callers who hold the create verb only at a led team's scope, not at the
 *  workspace scope.
 *
 *  Only the SHOWING of previously hidden controls rides this flag. The
 *  companion tightening — hiding controls whose write the backend refuses (a
 *  sub-team create for a non-lead, the "No parent" option for a caller without
 *  a workspace-level team create, the delivery-team options a caller cannot
 *  create under) — ships unflagged, the #5966 hide-only precedent: it removes
 *  a predictable authorization failure, not a capability.
 *
 *  The capability probes behind it (`project.create` / `team.create` with no
 *  `team_id`, answered from the workspace scope ∪ the caller's led teams) are
 *  ungated backend behaviour: they only ever feed an affordance, never
 *  authorize a write. Created manually in the LD UI; defaults **off** and is
 *  treated as off when the flag is absent. */
export const TEAM_LEAD_CREATE_FLAG = "team-lead-create";

/** Gates the Start of week control on Profile → Preferences (NG-2275). No view
 *  reads `Profile.start_of_the_week` yet, so turn it on only once they do.
 *  Hiding writes nothing, so users' saved choices survive. Created manually in
 *  the LD UI; defaults **off** and is treated as off when absent. */
export const START_OF_WEEK_FLAG = "start-of-week";

/** Gates the **Public holidays** card on Workspace Settings → Calendars
 *  (NG-2277). Inverted: the card had shipped, and off is what HIDES it, since
 *  Holiday calendars cover the same need. Only the UI is gated — the
 *  `holiday-calendar/me/` endpoint stays live, and a feed already generated keeps
 *  reducing capacity. Created manually in the LD UI; treated as off when absent. */
export const PUBLIC_HOLIDAYS_FLAG = "public-holidays";

/** Gates a person's own calendar connections (NG-2278): the "User calendar
 *  connections" card on workspace settings → Calendars, with its Connect Google /
 *  Connect Outlook buttons, sync and disconnect actions, and the toast shown on
 *  the OAuth redirect back. Ported from Plane and not validated, so it stays dark
 *  for the LA release. Holiday calendars and domain-wide sync are not behind it.
 *
 *  Only the UI is gated: the `users/me/calendar-*` endpoints stay live, and the
 *  30-minute OAuth sync keeps running for anyone already connected. Defaults
 *  **off** and is treated as off when absent. */
export const CALENDAR_CONNECTIONS_FLAG = "calendar-connections";

/** Gates the SAML Single Logout copy on the SSO provider form (NG-2047): the
 *  SLO URL hint and the sharpened Logout URL hint that explain the SLO-vs-
 *  plain-redirect precedence. The SLO behaviour itself only activates when a
 *  deployment configures SP signing keys, but the copy is visible UI and so
 *  merges dark. Created manually in the LD UI; defaults **off** and is treated
 *  as off when absent. */
export const SAML_SINGLE_LOGOUT_COPY_FLAG = "saml-single-logout-copy";

/** Gates Workspace Settings → Integrations → Webhooks: the nav entry, the list
 *  page and the webhook detail page (NG-2330). The settings tab registry carries
 *  it, so the layout hides the entry and redirects a deep link, for both
 *  `webhooks` and `webhooks/:webhookId`.
 *
 *  This one hides a page that already shipped, rather than holding back a new
 *  one. Only work-item events are ever delivered (`work_items/handlers.py` is the
 *  sole producer), so four of the editor's five event checkboxes never fire
 *  (NG-591), and prod had no deliveries and no webhook edits in the 30 days to
 *  2026-10-02. The backend is not gated: the API stays, and existing webhooks
 *  keep receiving work-item events.
 *
 *  Created in LaunchDarkly **off** in every environment (Dan, 2026-10-02); the
 *  hermetic e2e stack forces it on so the page keeps its coverage. Product
 *  decides when it returns. Treated as off when absent, which hides the page. */
export const WEBHOOKS_FLAG = "webhooks-settings";

/** Out-of-the-box roles (NG-3050, epic NG-2959). Same key as the backend's
 *  `TARGET_MODEL_OOTB_ROLES`, which seeds a new workspace the seven OOTB roles. The
 *  frontend half (NG-3087): every workspace role picker offers the roles the
 *  workspace holds that can be assigned, OOTB roles included, and preselects the
 *  workspace's default role. Off, the pickers offer Admin, Standard User and Guest
 *  (plus custom roles where they did) as today. Defaults **off**; ON in dev only. */
export const TARGET_MODEL_OOTB_ROLES_FLAG = "target-model-ootb-roles";

/** A program has one Program Owner (NG-3022, epic NG-2959, spec 5.1). Same key as the
 *  backend's `TARGET_MODEL_PROGRAM_OWNER`, which owns a new program by its creator when
 *  no Owner is named (NG-3162). Here the create dialog preselects the creator as Owner.
 *  ON in dev and staging, OFF in production; treated as off when absent. */
export const TARGET_MODEL_PROGRAM_OWNER_FLAG = "target-model-program-owner";

/** Roles and Permissions Target Model (epic NG-2959, NG-2963 / NG-2965): a project's
 *  own Workspace / Team restricted setting decides who sees it. Same key as the
 *  backend's ``TARGET_MODEL_PROJECT_PRIVACY``, evaluated per workspace, so the two
 *  halves turn on together. On, switching a project to Team restricted first shows
 *  its Lead who loses access, and the capacity chip reads "Restricted project".
 *  Off, the switch saves at once and the chip reads "Private project", as today. */
export const TARGET_MODEL_PROJECT_PRIVACY_FLAG = "target-model-project-privacy";

/** A plain click on another project's bar in the Plan work Gantt stays on
 *  the tab instead of navigating to that project's Overview (NG-1026). The
 *  scheduler's in-progress, unsaved plan is lost by the navigation today, so
 *  on, the unscored bar's link swallows primary-button clicks; cmd/ctrl/
 *  middle-click (open in a new tab), keyboard activation, the name column's
 *  link and a scored bar's priority breakdown are untouched. Off, a bar click
 *  navigates the current tab, as before. Treated as off when absent. */
export const PLAN_WORK_GANTT_BAR_NO_NAVIGATE_FLAG = "plan-work-gantt-bar-no-navigate";

/** The Plan work Gantt's project-name column paints its background behind
 *  every row (NG-1031). As a stretched flex item the column is only as tall as
 *  the chart's visible area, so once the list is longer than one screenful its
 *  opaque background stops there: the rows below are see-through, and a
 *  horizontally scrolled bar's own name label shows through the project name
 *  beside it — on a laptop-height window, after scrolling down a few rows. On,
 *  the column sizes to its rows (and still fills a short list); off, as before.
 *  Treated as off when absent. */
export const PLAN_WORK_GANTT_NAME_COLUMN_FILL_FLAG = "plan-work-gantt-name-column-fill";

/** Gates the fix for a nested (sub-task / sub-session) row selected in a
 *  project's Work items table (NG-3116, parent NG-2509). Today the page builds
 *  its bulk-action eligibility lookup from the ROOT list rows only; a selected
 *  nested row lives in its parent's lazily read children and so reads as unknown,
 *  and the fail-closed gates hide everything but Export. With the flag on, the
 *  page fetches each expanded parent's children with the new opt-in
 *  `expand_permissions` query param (so each child carries its own per-row
 *  delete/edit verdict, like a top-level row) and folds those children into the
 *  same eligibility lookup, so a nested selection offers the full bulk bar —
 *  Set status, Assignees, Archive, Delete, Export — exactly where the caller can
 *  actually perform each action. Off, the lookup stays root-only and a nested
 *  selection keeps today's Export-only bar. No backend authorization changes:
 *  the flag only surfaces the verdicts the write endpoints already enforce.
 *  Created manually in the LD UI; defaults **off** and is treated as off when
 *  absent. */
export const NESTED_WORK_ITEM_BULK_ACTIONS_FLAG = "nested-work-item-bulk-actions";
/** Pins the "This project" row of the Plan work Gantt below the date header
 *  (NG-1025). The row is the plan being configured, and the tab exists to
 *  compare it against the projects scheduled around it, so on, it stays in view
 *  while the project list scrolls under it. Off, it scrolls away with the rest
 *  of the list, as before. Treated as off when absent. */
export const PLAN_WORK_STICKY_THIS_PROJECT_FLAG = "plan-work-sticky-this-project";
